import React from 'react';
import { Icon } from './icons';
import { Flame } from './common';
import { StreakData, HeatmapData, Achievement, AnalyticsData } from '../types';

export const StatsScreen: React.FC<{
  streak: StreakData | null;
  heatmap: HeatmapData | null;
  achievements: Achievement[];
  analytics: AnalyticsData | null;
  onOpenReport: () => void;
}> = ({ streak, heatmap, achievements, analytics, onOpenReport }) => {
  const current = streak?.current ?? 0;
  const longest = streak?.longest ?? 0;
  const totals = analytics?.totals || { books: 0, finishedBooks: 0, pages: 0, hours: 0, readingBooks: 0 };
  const weeks = heatmap?.heatmap || [];

  return (
    <div className="scroll" style={{ paddingBottom: 120 }}>
      <div style={{ padding: '40px 20px 0' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Seu ritmo</div>
        <div className="serif" style={{ fontSize: 38, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
          Páginas viram<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>hábito</span>.
        </div>
      </div>

      <div style={{ padding: '24px 20px 0' }}>
        <button onClick={onOpenReport} style={{
          width: '100%', textAlign: 'left', background: 'var(--paper-2)',
          padding: 16, borderRadius: 18, marginBottom: 12,
          border: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: 'var(--ember-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Icon name="sparkle" size={18} color="var(--ember)"/></div>
          <div style={{ flex: 1 }}>
            <div className="serif" style={{ fontSize: 17, lineHeight: 1.1 }}>Relatório inteligente</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>Insights + sugestão de meta</div>
          </div>
          <Icon name="chevron-right" size={16} color="var(--ink-3)"/>
        </button>

        <div style={{
          background: 'var(--ink)', color: 'var(--paper)',
          borderRadius: 26, padding: 26, position: 'relative', overflow: 'hidden',
        }}>
          {/* Invariante 4 — nunca mostrar zero. O contador reinicia, mas a
              conquista é permanente: quando a sequência atual é zero, o número
              grande passa a ser a MELHOR marca, que ninguém tira de você. Um "0"
              em serif de 68px comunica que tudo foi perdido e dispara a escalada
              de abandono — quando, cientificamente, o dia perdido custou 0,29
              pontos numa escala de 42. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
                {current > 0 ? 'Sequência atual' : longest > 0 ? 'Sua melhor marca' : 'Sua sequência'}
              </div>
              <div className="serif" style={{ fontSize: 68, lineHeight: 1, marginTop: 8, letterSpacing: '-0.02em' }}>
                {current > 0 ? current : (longest > 0 ? longest : '—')}
                {(current > 0 || longest > 0) && <span style={{ fontSize: 22, color: 'var(--ember)', marginLeft: 8 }}>{(current > 0 ? current : longest) === 1 ? 'dia' : 'dias'}</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 8 }}>
                {current > 0
                  ? `recorde · ${longest} ${longest === 1 ? 'dia' : 'dias'}`
                  : longest > 0
                    ? 'uma leitura hoje começa a próxima'
                    : 'sua primeira página começa aqui'}
              </div>
            </div>
            <Flame count={current > 0 ? current : longest} size={60}/>
          </div>

          {weeks.length > 0 && (
            <>
              <div style={{ marginTop: 22, display: 'flex', gap: 3 }}>
                {weeks.map((w, wi) => (
                  <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                    {w.map((d, di) => {
                      const intensity = d.pages > 0 ? Math.min(1, d.pages / 30) : 0;
                      return (
                        <div key={di} style={{
                          aspectRatio: '1', borderRadius: 2,
                          background: intensity > 0 ? 'var(--ember)' : 'rgba(255,255,255,0.06)',
                          opacity: intensity > 0 ? (0.3 + intensity * 0.7) : 1,
                        }}/>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', marginTop: 8,
                fontSize: 9, color: 'var(--ink-4)', fontFamily: 'Geist Mono, monospace', letterSpacing: '0.1em',
              }}>
                {weeks.length >= 26 ? <><span>OUT</span><span>NOV</span><span>DEZ</span><span>JAN</span><span>FEV</span><span>MAR</span><span>ABR</span></> : null}
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: '24px 20px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <StatCard label="Páginas / dia" value={String(analytics?.avgPerDay ?? 0)} hint="média" accent="ember"/>
        <StatCard label="Livros" value={String(totals.finishedBooks)} hint="terminados" accent="sage"/>
        <StatCard label="Horas" value={String(totals.hours)} hint="aproximadas"/>
        <StatCard label="Sessões" value={String(analytics?.totalSessions ?? 0)} hint="registradas"/>
      </div>

      <div className="section-head">
        <div>
          <div className="label">Conquistas</div>
          <div className="serif title" style={{ marginTop: 4 }}>Marcos</div>
        </div>
      </div>
      <div style={{ padding: '0 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {achievements.map(a => (
          <div key={a.id} style={{
            padding: 16, borderRadius: 18,
            background: a.unlocked ? 'var(--paper-2)' : 'transparent',
            border: '1px solid var(--line)',
            opacity: a.unlocked ? 1 : 0.5,
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: a.unlocked ? 'var(--ember-soft)' : 'var(--paper-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><Icon name={a.icon} size={20} color={a.unlocked ? 'var(--ember)' : 'var(--ink-4)'}/></div>
            <div className="serif" style={{ fontSize: 17, marginTop: 10, lineHeight: 1.1 }}>{a.title}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{a.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

function StatCard({ label, value, hint, accent }: { label: string; value: string; hint: string; accent?: 'ember' | 'sage' }) {
  const color = accent === 'ember' ? 'var(--ember)' : accent === 'sage' ? 'var(--sage)' : 'var(--ink)';
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{label}</div>
      <div className="serif" style={{ fontSize: 36, lineHeight: 1.05, marginTop: 6, color, letterSpacing: '-0.02em' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{hint}</div>
    </div>
  );
}
